import {openSupport} from '../Support/messenger';
/* eslint-disable react-refresh/only-export-components -- file exports multiple helpers alongside components; splitting is not worth the churn */

import {ErrorInfo, PropsWithChildren, ReactElement, ReactNode, useState, useSyncExternalStore} from 'react';
import './ErrorBoundary.less';
import IconButton from "../ui/IconButton/IconButton";
import Icons from "../ui/IconButton/Icons";
import type {FallbackProps} from "react-error-boundary";
import {ErrorBoundary} from "react-error-boundary";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Button} from '@mui/material';
import {captureException} from "../lib/picos/error";
import {getPendingRecovery, isChunkLoadError, reloadForStaleChunk, reloadNow, subscribePendingRecovery} from "../lib/chunkReload";

export type FallbackRenderFn = (props: FallbackProps) => ReactNode;

export type ErrorBoundaryBaseProps = {
	// If you want to handle the error messaging yourself.  This is checked first.
	fallbackRender?: FallbackRenderFn,

	// For larger areas, where a larger message is appropriate. This is checked second.
	shouldUseErrorFrame?: boolean,

	// For smallest areas, just put a little error icon with a hint
	shouldUseErrorIcon?: boolean,

	// A unique key used to reset the captured error when the protected target changes
	uniqueKey: string
};

export type ErrorBoundaryProps = PropsWithChildren<ErrorBoundaryBaseProps>;

/**
 * Error handling wrapper.
 *
 * By default, an error in a component will simply show an error icon where the error occurred, with an error popup.
 *
 * You can override this behavior in two ways:
 * 1. You can ask for a standardized error frame using shouldUseErrorFrame.  This provides a nice, user-friendly section
 *    explaining that something went wrong, and that it is being looked at.
 * 2. You can provide a custom error renderer using the fallbackRender callback. This should provide a React component
 *    that will be displayed in lieu of the error icon.
 * 3. You can just let it draw an error icon with a popup message for what went wrong.
 *
 * In all cases, the error will be logged to console.error.
 *
 * Support reports are initiated by the user; error details are not automatically sent to chat.
 *
 * @param {FallbackProps} props
 * @returns {ReactElement}
 * @constructor
 */
export function ErrorFrame(props: FallbackProps): ReactElement {
	const [showError, setShowError] = useState(false);
	const isStaleDeploy = isChunkLoadError(props.error);
	// OWA-828: while chunkReload has a retry scheduled, say so instead of
	// presenting a dead end. Subscribed, because the schedule is written by
	// onError, which runs AFTER this fallback first renders.
	const retrying = useSyncExternalStore(subscribePendingRecovery, getPendingRecovery);

	const onViewClick = () => {
		setShowError(!showError);
	};

	// OWA-828: a user-initiated reload resets the whole recovery episode and
	// always performs a real reload. The old handler cleared only one of two
	// cooldown flags, so a click inside the cooldown was a visible no-op.
	const onReloadClick = () => reloadNow();

	return <div className={`error-boundary${isStaleDeploy ? ' error-boundary--update' : ''}`} role={isStaleDeploy ? 'status' : 'alert'}>

		{/* Title */}
		<div className='error-boundary__title'>
			<IconButton type={isStaleDeploy ? Icons.RETRY : Icons.AMBULANCE} color={isStaleDeploy ? '#1565c0' : 'maroon'}
			            label={isStaleDeploy ? (retrying ? 'Loading the latest version' : 'A new version is available') : 'Whoah'}/>
		</div>

		{/* Standard Message */}
		<div className='error-boundary__message'>
			{isStaleDeploy ? (
				<>
					{retrying ? (
						<p>Outlaw Practice was updated a moment ago and part of the app has not
							finished arriving. Retrying automatically
							(attempt {retrying.attempt} of {retrying.maxAttempts}). You can also reload now.</p>
					) : (
						<p>Outlaw Practice has been updated since you loaded this page.
							Reload to get the latest version.</p>
					)}
					<p>
						<Button variant='text' size='small' color='primary' onClick={onReloadClick}>Reload Now</Button>
					</p>
				</>
			) : (
				<>
					<p>Looks like something unexpected happened.</p>

					<p className='u-row-runin'>Please click &nbsp;
						<a href='https://support.outlawpractice.com' onClick={event => {event.preventDefault(); openSupport({newConversation: true});}}>
							<FontAwesomeIcon className='error-boundary__icon' icon={Icons.SUPPORT.reactIcon}/> here
						</a> to start a new support conversation, or send an email to <a
							href='mailto:support@outlawpractice.com'>
							support@outlawpractice.com
						</a>.
					</p>
				</>
			)}
		</div>

		{/* Toggle Error Details */}
		{!isStaleDeploy && <div className='error-boundary__show-error'>
			<Button variant='text' size='small' color='primary' onClick={onViewClick} aria-expanded={showError}>
				{showError ? 'Hide' : 'View'} Error Details
			</Button>
			{showError && <div className='error-boundary__error-message'>
				<div>{props.error instanceof Error ? props.error.message : String(props.error)}</div>
				<pre>{props.error instanceof Error ? props.error.stack : ''}</pre>
			</div>}
		</div>}
	</div>
}

/**
 * Log an error to the remote system.
 *
 * @param {Error} error
 * @param {ErrorInfo} info
 */
export function logError(error: Error, info: ErrorInfo) {
	// First, report to the console
	console.error(error);
	console.error(info);

	// If this is a stale-deploy chunk load failure, try to recover by reloading
	// before the user is left staring at the error frame.
	if (isChunkLoadError(error)) reloadForStaleChunk(error, 'ErrorBoundaryO');

	// Report the error to Sentry
	captureException(error, {tags: ['ErrorBoundaryO'], extra: {}});

}

/**
 * Default fallback render.  Just show the error message.
 * @param {FallbackProps} props
 * @returns {ReactElement}
 */
export const defaultFallbackRender = (props: FallbackProps): ReactElement => {
	return <p className='error-boundary__error-message'>{props.error?.toString()}</p>;
}

/**
 * Render an error frame.
 *
 * @param {FallbackProps} props
 * @returns {ReactElement}
 */
export const errorFrameRender = (props: FallbackProps): ReactElement => {
	return <ErrorFrame {...props}/>;
}

/**
 *
 * @param {Error} error
 * @returns {Element}
 */
export const iconErrorRender = (error) => {
	return <div className='error-boundary--icon'>
		<FontAwesomeIcon icon={Icons.AMBULANCE.reactIcon}/>
		<div className='error-boundary--icon-error'>{error?.message || 'Unknown Error'}</div>
	</div>;
}

/**
 * Error handling wrapper.
 *
 * @param {ErrorBoundaryProps} props
 * @constructor
 */
function ErrorBoundaryO(props: ErrorBoundaryProps) {
	const {shouldUseErrorFrame, shouldUseErrorIcon = true} = props;

	// Use the fallbackRender passed in props if available
	let fallbackRender = props.fallbackRender;
	if (!fallbackRender && shouldUseErrorFrame) fallbackRender = errorFrameRender;
	if (!fallbackRender && shouldUseErrorIcon) fallbackRender = iconErrorRender;
	if (!fallbackRender) fallbackRender = defaultFallbackRender;

	return <ErrorBoundary fallbackRender={fallbackRender}
						  onError={logError}
						  resetKeys={[props.uniqueKey]}
	>
		{props.children}
	</ErrorBoundary>;
}

export default ErrorBoundaryO;
